/**
 * useTheatreDetail — React Query hook for theatre detail + mutations.
 *
 * Fetches a single theatre by ID and exposes commit/run/settle mutations.
 * Also fetches commitment receipt and certificate when applicable.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { theatreApi } from '../api/theatres';
import type {
  TheatreResponse,
  CommitmentReceiptResponse,
  TheatreCertificateResponse,
  ActivationReadinessResponse,
  RuntimeReadinessResponse,
  DatasetBindingReadinessResponse,
} from '../types/theatre';

export function useTheatreDetail(
  theatreId: string | null,
  options?: { includeInvestigationContext?: boolean },
) {
  const queryClient = useQueryClient();

  // ── Theatre detail ──────────────────────────────────────────────────
  const theatreQuery = useQuery<TheatreResponse>({
    queryKey: ['theatre', theatreId, options?.includeInvestigationContext ? 'investigation_context' : 'base'],
    queryFn: () => theatreApi.getTheatre(theatreId!, options),
    enabled: !!theatreId,
    staleTime: 10_000,
  });

  const theatre = theatreQuery.data;

  // ── Commitment receipt (only for COMMITTED+ theatres) ───────────────
  const commitmentQuery = useQuery<CommitmentReceiptResponse>({
    queryKey: ['theatre', theatreId, 'commitment'],
    queryFn: () => theatreApi.getCommitment(theatreId!),
    enabled: !!theatreId && !!theatre?.commitment_hash,
    staleTime: 60_000, // Commitment is immutable once set
  });

  // ── Certificate (only for RESOLVED theatres) ────────────────────────
  const certificateQuery = useQuery<TheatreCertificateResponse>({
    queryKey: ['theatre', theatreId, 'certificate'],
    queryFn: () => theatreApi.getTheatreCertificate(theatreId!),
    enabled: !!theatreId && theatre?.state === 'RESOLVED',
    staleTime: 60_000, // Certificate is immutable once issued
  });

  // ── Readiness queries (conditional on state) ────────────────────────

  const activationReadinessQuery = useQuery<ActivationReadinessResponse>({
    queryKey: ['theatre', theatreId, 'activation-readiness'],
    queryFn: () => theatreApi.getActivationReadiness(theatreId!),
    enabled: !!theatreId && theatre?.state === 'MATERIALIZED',
    staleTime: 15_000,
  });

  const runtimeReadinessQuery = useQuery<RuntimeReadinessResponse>({
    queryKey: ['theatre', theatreId, 'runtime-readiness'],
    queryFn: () => theatreApi.getRuntimeReadiness(theatreId!),
    enabled: !!theatreId && theatre?.state === 'COMMITTED',
    staleTime: 15_000,
  });

  const datasetBindingReadinessQuery = useQuery<DatasetBindingReadinessResponse>({
    queryKey: ['theatre', theatreId, 'dataset-binding-readiness'],
    queryFn: () => theatreApi.getDatasetBindingReadiness(theatreId!),
    enabled: !!theatreId && theatre?.state === 'COMMITTED' && theatre?.inquiry_class === 'FORGE_EVALUATION',
    staleTime: 15_000,
  });

  // ── Mutations ───────────────────────────────────────────────────────

  const invalidateTheatre = () => {
    queryClient.invalidateQueries({ queryKey: ['theatre', theatreId] });
  };

  const commitMutation = useMutation({
    mutationFn: () => theatreApi.commitTheatre(theatreId!),
    onSuccess: () => {
      invalidateTheatre();
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'commitment'],
      });
    },
  });

  const runMutation = useMutation({
    mutationFn: () => theatreApi.runTheatre(theatreId!),
    onSuccess: invalidateTheatre,
  });

  const settleMutation = useMutation({
    mutationFn: () => theatreApi.settleTheatre(theatreId!),
    onSuccess: () => {
      invalidateTheatre();
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'certificate'],
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: () => theatreApi.activateTheatre(theatreId!),
    onSuccess: () => {
      invalidateTheatre();
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'activation-readiness'],
      });
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'commitment'],
      });
    },
  });

  const bindDatasetsMutation = useMutation({
    mutationFn: () => theatreApi.bindDatasets(theatreId!),
    onSuccess: () => {
      invalidateTheatre();
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'dataset-binding-readiness'],
      });
      queryClient.invalidateQueries({
        queryKey: ['theatre', theatreId, 'runtime-readiness'],
      });
    },
  });

  const startRuntimeMutation = useMutation({
    mutationFn: () => theatreApi.startRuntime(theatreId!),
    onSuccess: invalidateTheatre,
  });

  return {
    // Data
    theatre: theatre ?? null,
    commitment: commitmentQuery.data ?? null,
    certificate: certificateQuery.data ?? null,

    // Readiness
    activationReadiness: activationReadinessQuery.data ?? null,
    runtimeReadiness: runtimeReadinessQuery.data ?? null,
    datasetBindingReadiness: datasetBindingReadinessQuery.data ?? null,

    // Loading states
    isLoading: theatreQuery.isLoading,
    isCommitmentLoading: commitmentQuery.isLoading,
    isCertificateLoading: certificateQuery.isLoading,

    // Errors
    error: theatreQuery.error,

    // Standard mutations
    commit: commitMutation.mutateAsync,
    isCommitting: commitMutation.isPending,
    run: runMutation.mutateAsync,
    isRunning: runMutation.isPending,
    settle: settleMutation.mutateAsync,
    isSettling: settleMutation.isPending,

    // FORGE lifecycle mutations
    activate: activateMutation.mutateAsync,
    isActivating: activateMutation.isPending,
    bindDatasets: bindDatasetsMutation.mutateAsync,
    isBindingDatasets: bindDatasetsMutation.isPending,
    startRuntime: startRuntimeMutation.mutateAsync,
    isStartingRuntime: startRuntimeMutation.isPending,
  };
}
